import { create } from "zustand";
import { CompanyMatchUploadResponse } from "../types/companyMatch";
import { STORAGE_KEY_PREFIX, PROCESSING_TYPES } from "./const";

export type MatchDataProcessingType = "whitespace" | "uploadedAccounts";

interface ProcessingFileData extends CompanyMatchUploadResponse {
  type: MatchDataProcessingType;
}

interface MatchDataProcessingState {
  // Map of workspaceId -> processing information
  processingFiles: Record<string, ProcessingFileData>;

  // Actions
  startProcessing: (
    workspaceId: string,
    fileData: CompanyMatchUploadResponse,
    type: MatchDataProcessingType
  ) => void;
  updateProcessingData: (
    workspaceId: string,
    fileData: Partial<CompanyMatchUploadResponse>
  ) => void;
  stopProcessing: (workspaceId: string, type?: MatchDataProcessingType) => void;
  getProcessingFile: (
    workspaceId: string,
    type?: MatchDataProcessingType
  ) => ProcessingFileData | null;
  loadFromLocalStorage: (workspaceId: string) => void;
}

export const useMatchDataProcessingStore = create<MatchDataProcessingState>(
  (set, get) => ({
    processingFiles: {},

    startProcessing: (workspaceId, fileData, type) => {
      const dataWithType: ProcessingFileData = {
        ...fileData,
        type,
      };

      // Save to localStorage with type in the key
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}${workspaceId}_${type}`,
        JSON.stringify(dataWithType)
      );

      // Update state
      set((state) => ({
        processingFiles: {
          ...state.processingFiles,
          [`${workspaceId}_${type}`]: dataWithType,
        },
      }));
    },

    updateProcessingData: (workspaceId, updatedData) => {
      // Find the processing file by checking both types
      let key: string | null = null;
      let currentData: ProcessingFileData | null = null;

      for (const type of PROCESSING_TYPES) {
        const testKey = `${workspaceId}_${type}`;
        if (get().processingFiles[testKey]) {
          key = testKey;
          currentData = get().processingFiles[testKey];
          break;
        }
      }

      if (!currentData || !key) return;

      const newData = { ...currentData, ...updatedData };

      // Update localStorage
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}${key}`,
        JSON.stringify(newData)
      );

      // Update state
      set((state) => ({
        processingFiles: {
          ...state.processingFiles,
          [key]: newData,
        },
      }));
    },

    stopProcessing: (workspaceId, type) => {
      if (type) {
        // Remove specific type
        const key = `${workspaceId}_${type}`;
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);

        set((state) => {
          const newProcessingFiles = { ...state.processingFiles };
          delete newProcessingFiles[key];
          return { processingFiles: newProcessingFiles };
        });
      } else {
        // Remove all types for this workspace
        for (const processingType of PROCESSING_TYPES) {
          const key = `${workspaceId}_${processingType}`;
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
        }

        set((state) => {
          const newProcessingFiles = { ...state.processingFiles };
          for (const processingType of PROCESSING_TYPES) {
            const key = `${workspaceId}_${processingType}`;
            delete newProcessingFiles[key];
          }
          return { processingFiles: newProcessingFiles };
        });
      }
    },

    getProcessingFile: (workspaceId, type) => {
      if (type) {
        // Get specific type
        const key = `${workspaceId}_${type}`;

        // First check in state
        if (get().processingFiles[key]) {
          return get().processingFiles[key];
        }

        // If not found, try to recover from localStorage
        try {
          const storedData = localStorage.getItem(
            `${STORAGE_KEY_PREFIX}${key}`
          );
          if (storedData) {
            const parsedData = JSON.parse(storedData);

            // Silently update state
            set((state) => ({
              processingFiles: {
                ...state.processingFiles,
                [key]: parsedData,
              },
            }));

            return parsedData;
          }
        } catch (error) {
          console.error(`Error parsing ${type} data from localStorage:`, error);
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
        }
      } else {
        // Get any type for this workspace
        for (const processingType of PROCESSING_TYPES) {
          const result = get().getProcessingFile(workspaceId, processingType);
          if (result) return result;
        }
      }

      return null;
    },

    loadFromLocalStorage: (workspaceId) => {
      // Load processing files for this workspace from localStorage
      for (const type of PROCESSING_TYPES) {
        const key = `${workspaceId}_${type}`;
        const storageKey = `${STORAGE_KEY_PREFIX}${key}`;

        try {
          const storedData = localStorage.getItem(storageKey);
          if (storedData) {
            const parsedData = JSON.parse(storedData);

            set((state) => ({
              processingFiles: {
                ...state.processingFiles,
                [key]: parsedData,
              },
            }));
          }
        } catch (error) {
          console.error(`Error loading ${type} data from localStorage:`, error);
        }
      }
    },
  })
);
